import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { MoreVertical, Pencil, ArrowLeftRight, Trash2, GripVertical, Star } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  MACRO_COLORS,
  MACRO_KCAL,
  MEAL_SLOT_META,
  MEAL_SLOT_ORDER,
  type BubbleEntry,
  type Macro,
  type MealSlot,
} from "@/lib/foods";
import foodPresets from "@/data/food-presets.json";
import { cloudRepository } from "@/lib/repository/cloud";
import type { FoodRow } from "@/lib/repository/types";

interface FoodPreset {
  id: string;
  name: string;
  kcal: number;
  carb: number;
  protein: number;
  fat: number;
  serving_g: number;
}
const PRESETS = foodPresets as FoodPreset[];

interface Baseline {
  name: string;
  kcal: number;
  carb: number;
  protein: number;
  fat: number;
  serving_g: number;
}

interface CustomFoodLite {
  name: string;
  kcal: number;
  carb_g: number;
  protein_g: number;
  fat_g: number;
  serving_g: number;
}

function readCustomFoods(): CustomFoodLite[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem("customFoods") || "[]");
  } catch {
    return [];
  }
}

function findBaselineByName(name: string): Baseline | undefined {
  const lower = name.toLowerCase();
  const preset =
    PRESETS.find((p) => p.name.split(" ")[0] === name) ??
    PRESETS.find((p) => p.name.toLowerCase() === lower);
  if (preset) {
    return {
      name: preset.name,
      kcal: preset.kcal,
      carb: preset.carb,
      protein: preset.protein,
      fat: preset.fat,
      serving_g: preset.serving_g,
    };
  }
  const c = readCustomFoods().find((x) => x.name.toLowerCase() === lower);
  if (c) {
    return {
      name: c.name,
      kcal: c.kcal,
      carb: c.carb_g,
      protein: c.protein_g,
      fat: c.fat_g,
      serving_g: c.serving_g,
    };
  }
  return undefined;
}

interface Props {
  entries: BubbleEntry[];
  onChangeSlot: (foodLogId: string, slot: MealSlot) => void;
  onDelete: (foodLogId: string, foodName: string) => void;
  onReplaceQty: (foodLogId: string, newEntries: BubbleEntry[]) => void;
  /** 즐겨찾기 food_id Set — ActionSheet의 "즐겨찾기 추가/해제" 라벨·동작 분기 */
  favFoodIds?: Set<string>;
  /** 즐겨찾기 토글 콜백 — food_id 인자. 미전달 시 ActionSheet에 즐겨찾기 옵션 미노출 */
  onToggleFavorite?: (foodId: string) => void;
}

interface LogItem {
  foodLogId: string;
  foodName: string;
  carbs: number;
  protein: number;
  fat: number;
  kcal: number;
  addedAt: number;
  slot: MealSlot;
  food_id?: string;
  /** 원본 로그의 저장 kcal(공식) — 매크로 Atwater 대신 표시·편집 기준으로 사용. */
  logKcal?: number;
  /** 원본 로그의 저장 grams — 편집 시트 시작값. */
  logGrams?: number;
}

function dominantMacro(item: LogItem): Macro {
  const arr: [Macro, number][] = [
    ["carbs", item.carbs],
    ["protein", item.protein],
    ["fat", item.fat],
  ];
  arr.sort((a, b) => b[1] - a[1]);
  return arr[0][0];
}

export function MealLogList({
  entries,
  onChangeSlot,
  onDelete,
  onReplaceQty,
  favFoodIds,
  onToggleFavorite,
}: Props) {
  // Expose onDelete ref so DraggableLogRow can call it without prop-drilling through DndContext
  const onDeleteRef = useRef(onDelete);
  onDeleteRef.current = onDelete;
  const [actionFor, setActionFor] = useState<LogItem | null>(null);
  const [slotSheetFor, setSlotSheetFor] = useState<LogItem | null>(null);
  const [editFor, setEditFor] = useState<LogItem | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    // 가벼운 터치/스크롤로 drag 오발동 방지 — 꾹 눌러야 활성화
    useSensor(PointerSensor, {
      activationConstraint: { delay: 250, tolerance: 8 },
    }),
  );

  const grouped = useMemo(() => {
    const byLog = new Map<string, LogItem>();
    for (const e of entries) {
      const slot: MealSlot = e.meal_slot ?? "snack";
      const cur = byLog.get(e.foodLogId);
      if (!cur) {
        byLog.set(e.foodLogId, {
          foodLogId: e.foodLogId,
          foodName: e.foodName,
          carbs: e.macro === "carbs" ? e.grams : 0,
          protein: e.macro === "protein" ? e.grams : 0,
          fat: e.macro === "fat" ? e.grams : 0,
          kcal: 0,
          addedAt: e.addedAt,
          slot,
          food_id: e.food_id,
          logKcal: e.logKcal,
          logGrams: e.logGrams,
        });
      } else {
        if (e.macro === "carbs") cur.carbs += e.grams;
        if (e.macro === "protein") cur.protein += e.grams;
        if (e.macro === "fat") cur.fat += e.grams;
        cur.addedAt = Math.max(cur.addedAt, e.addedAt);
      }
    }
    const items = Array.from(byLog.values()).map((it) => ({
      ...it,
      // 공식 로그 kcal 우선(식약처 등 매크로 불완전 음식도 정확·일관). 없으면 Atwater 폴백.
      kcal:
        it.logKcal ??
        Math.round(
          it.carbs * MACRO_KCAL.carbs + it.protein * MACRO_KCAL.protein + it.fat * MACRO_KCAL.fat,
        ),
    }));
    const groups: Record<MealSlot, LogItem[]> = {
      breakfast: [],
      lunch: [],
      dinner: [],
      snack: [],
    };
    for (const it of items) groups[it.slot].push(it);
    for (const slot of MEAL_SLOT_ORDER) {
      groups[slot].sort((a, b) => b.addedAt - a.addedAt);
    }
    return groups;
  }, [entries]);

  const itemById = useMemo(() => {
    const m = new Map<string, LogItem>();
    for (const slot of MEAL_SLOT_ORDER) {
      for (const it of grouped[slot]) m.set(it.foodLogId, it);
    }
    return m;
  }, [grouped]);

  const isDragging = activeId !== null;
  const activeItem = activeId ? itemById.get(activeId) ?? null : null;

  function handleDragStart(e: DragStartEvent) {
    setActiveId(String(e.active.id));
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(15);
  }

  function handleDragEnd(e: DragEndEvent) {
    const id = String(e.active.id);
    setActiveId(null);
    const item = itemById.get(id);
    if (!item || !e.over) return;
    const target = e.over.id as MealSlot;
    if (!MEAL_SLOT_ORDER.includes(target)) return;
    if (target === item.slot) return;
    onChangeSlot(item.foodLogId, target);
    // 자명한 시각 변화 — 토스트 생략 (UI에서 아이템이 즉시 옮겨감)
  }

  function openActions(item: LogItem) {
    setActionFor(item);
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <section className="px-5 mt-2">
          <div
            className="rounded-2xl border border-neutral-100 bg-white"
          >
            {MEAL_SLOT_ORDER.map((slot, idx) => {
              const items = grouped[slot];
              const meta = MEAL_SLOT_META[slot];
              const slotKcal = items.reduce((s, x) => s + x.kcal, 0);
              return (
                <div
                  key={slot}
                  className={idx > 0 ? "border-t border-neutral-100" : ""}
                >
                  <SlotDropZone slot={slot} isDragging={isDragging}>
                    <div className="px-4 py-1.5 text-[12px] font-semibold text-neutral-700 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5">
                        <meta.Icon className="h-3.5 w-3.5 text-neutral-500" strokeWidth={2.2} />
                        {meta.label}
                      </span>
                      <span className="text-neutral-400 tabular-nums">{slotKcal} kcal</span>
                    </div>
                    {items.length === 0 ? (
                      <div className="px-4 py-2 text-[12px] text-neutral-300">
                        {isDragging ? "여기에 놓아 옮기기" : "비어 있음"}
                      </div>
                    ) : (
                      <AnimatePresence initial={false}>
                        {items.map((it) => (
                          <DraggableLogRow
                            key={it.foodLogId}
                            item={it}
                            onOpenActions={() => openActions(it)}
                            onDelete={(id, name) => onDeleteRef.current(id, name)}
                          />
                        ))}
                      </AnimatePresence>
                    )}
                  </SlotDropZone>
                </div>
              );
            })}
          </div>
        </section>

        <DragOverlay dropAnimation={null}>
          {activeItem ? <LogRowVisual item={activeItem} dragging /> : null}
        </DragOverlay>
      </DndContext>

      {actionFor && (
        <ActionSheet
          item={actionFor}
          canEdit={true}
          isFavorite={
            actionFor.food_id && favFoodIds ? favFoodIds.has(actionFor.food_id) : undefined
          }
          showFavorite={!!onToggleFavorite && !!actionFor.food_id}
          onClose={() => setActionFor(null)}
          onPick={(action) => {
            const target = actionFor;
            setActionFor(null);
            if (action === "edit") setEditFor(target);
            else if (action === "slot") setSlotSheetFor(target);
            else if (action === "delete") onDelete(target.foodLogId, target.foodName);
            else if (action === "favorite" && target.food_id && onToggleFavorite) {
              onToggleFavorite(target.food_id);
            }
          }}
        />
      )}

      {slotSheetFor && (
        <SlotSheet
          item={slotSheetFor}
          onClose={() => setSlotSheetFor(null)}
          onPick={(slot) => {
            const target = slotSheetFor;
            setSlotSheetFor(null);
            if (slot === target.slot) return;
            onChangeSlot(target.foodLogId, slot);
            // 자명한 시각 변화 — 토스트 생략
          }}
        />
      )}

      {editFor && (
        <EditQuantitySheet
          item={editFor}
          onClose={() => setEditFor(null)}
          onConfirm={(newEntries) => {
            const target = editFor;
            setEditFor(null);
            onReplaceQty(target.foodLogId, newEntries);
            // 자명한 시각 변화 — 토스트 생략 (값이 즉시 갱신됨)
          }}
        />
      )}
    </>
  );
}

function SlotDropZone({
  slot,
  isDragging,
  children,
}: {
  slot: MealSlot;
  isDragging: boolean;
  children: React.ReactNode;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: slot });
  return (
    <motion.div
      ref={setNodeRef}
      layout
      className="py-2 transition-all"
      animate={{
        backgroundColor: isOver ? "rgba(243,244,246,1)" : "rgba(255,255,255,0)",
        scale: isOver ? 1.01 : 1,
      }}
      style={{
        borderRadius: 12,
        outline: isDragging && isOver ? "2px dashed rgba(115,115,115,0.4)" : "none",
        outlineOffset: -4,
      }}
    >
      {children}
    </motion.div>
  );
}


function DraggableLogRow({
  item,
  onOpenActions,
  onDelete,
}: {
  item: LogItem;
  onOpenActions: () => void;
  onDelete: (foodLogId: string, foodName: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.foodLogId,
  });
  const [primed, setPrimed] = useState(false);
  const timerRef = useRef<number | null>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (isDragging) setPrimed(false);
  }, [isDragging]);

  useEffect(() => () => clearTimer(), []);

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0 : 1,
    // vertical scroll 양보 — dnd-kit은 long-press 250ms 후 preventDefault로 capture
    touchAction: "pan-y",
  };

  // Compose with dnd-kit's listeners so drag activation still works.
  const dndListeners = listeners ?? {};
  const composed = {
    onPointerDown: (e: React.PointerEvent) => {
      dndListeners.onPointerDown?.(e);
      clearTimer();
      startPos.current = { x: e.clientX, y: e.clientY };
      timerRef.current = window.setTimeout(() => {
        setPrimed(true);
        if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(15);
      }, 300);
    },
    onPointerMove: (e: React.PointerEvent) => {
      dndListeners.onPointerMove?.(e);
      if (primed || !startPos.current) return;
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      if (dx * dx + dy * dy > 64) clearTimer(); // ~8px tolerance
    },
    onPointerUp: (e: React.PointerEvent) => {
      dndListeners.onPointerUp?.(e);
      clearTimer();
      setPrimed(false);
      startPos.current = null;
    },
    onPointerCancel: (e: React.PointerEvent) => {
      dndListeners.onPointerCancel?.(e);
      clearTimer();
      setPrimed(false);
      startPos.current = null;
    },
    onKeyDown: dndListeners.onKeyDown as React.KeyboardEventHandler<HTMLDivElement> | undefined,
  };

  return (
    <motion.div
      ref={setNodeRef}
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{
        opacity: isDragging ? 0 : 1,
        scale: primed ? 1.02 : 1,
      }}
      transition={{ scale: { type: "spring", stiffness: 400, damping: 28 } }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      style={style}
      {...attributes}
      onContextMenu={(e) => e.preventDefault()}
      className={`select-none rounded-xl transition-shadow ${
        primed ? "bg-white shadow-lg ring-1 ring-neutral-200" : ""
      }`}
    >
      <LogRowVisual
        item={item}
        onOpenActions={onOpenActions}
        dragHandle={composed}
      />
    </motion.div>
  );
}

function LogRowVisual({
  item,
  dragging = false,
  onOpenActions,
  dragHandle,
}: {
  item: LogItem;
  dragging?: boolean;
  onOpenActions?: () => void;
  dragHandle?: React.HTMLAttributes<HTMLElement>;
}) {
  const dot = MACRO_COLORS[dominantMacro(item)];
  return (
    <div
      className={`flex items-center gap-2 px-2 py-2 ${
        dragging ? "bg-white rounded-xl shadow-xl scale-[1.03]" : "active:bg-neutral-50"
      }`}
    >
      {dragHandle && (
        <button
          type="button"
          aria-label="순서 변경"
          {...dragHandle}
          style={{ touchAction: "none", cursor: "grab" }}
          className="flex h-8 w-6 items-center justify-center text-neutral-300 hover:text-neutral-500"
        >
          <GripVertical size={16} />
        </button>
      )}
      <span
        className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
        style={{ background: dot }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-bold text-neutral-900 truncate">{item.foodName}</div>
        <div className="text-[12px] text-neutral-400 mt-0.5">
          탄 {Math.round(item.carbs)}g · 단 {Math.round(item.protein)}g · 지 {Math.round(item.fat)}g
        </div>
      </div>
      <div className="text-[14px] font-bold tabular-nums text-neutral-900">{item.kcal}</div>
      {onOpenActions && (
        <button
          type="button"
          aria-label="더보기"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onOpenActions();
          }}
          className="p-1.5 -mr-1 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 active:scale-95 transition flex items-center justify-center"
          style={{ minWidth: 32, minHeight: 32 }}
        >
          <MoreVertical size={16} />
        </button>
      )}
    </div>
  );
}

/* ----------------------------- Action Sheet ----------------------------- */

type ActionKind = "edit" | "slot" | "delete" | "favorite";

function ActionSheet({
  item,
  canEdit,
  isFavorite,
  showFavorite,
  onClose,
  onPick,
}: {
  item: LogItem;
  canEdit: boolean;
  /** 현재 즐겨찾기 여부 — undefined면 모름 (즐겨찾기 옵션 자체 미노출) */
  isFavorite?: boolean;
  /** 즐겨찾기 옵션 노출 여부 — food_id가 있고 toggle 콜백 있을 때만 true */
  showFavorite: boolean;
  onClose: () => void;
  onPick: (action: ActionKind) => void;
}) {
  return (
    <SheetShell onClose={onClose} title="이 음식" subtitle={item.foodName}>
      <button
        disabled={!canEdit}
        onClick={() => onPick("edit")}
        className="w-full h-12 rounded-xl text-left px-4 text-[15px] font-medium text-neutral-800 hover:bg-neutral-50 active:scale-[0.98] transition disabled:opacity-40 flex items-center gap-3"
      >
        <Pencil className="h-[18px] w-[18px] text-neutral-600" strokeWidth={2} />
        수량 편집
      </button>
      <button
        onClick={() => onPick("slot")}
        className="w-full h-12 rounded-xl text-left px-4 text-[15px] font-medium text-neutral-800 hover:bg-neutral-50 active:scale-[0.98] transition flex items-center gap-3"
      >
        <ArrowLeftRight className="h-[18px] w-[18px] text-neutral-600" strokeWidth={2} />
        슬롯 변경
      </button>
      {showFavorite && (
        <button
          onClick={() => onPick("favorite")}
          className="w-full h-12 rounded-xl text-left px-4 text-[15px] font-medium text-neutral-800 hover:bg-neutral-50 active:scale-[0.98] transition flex items-center gap-3"
        >
          <Star
            className="h-[18px] w-[18px]"
            fill={isFavorite ? "#FFD700" : "none"}
            stroke={isFavorite ? "#FFD700" : "#525252"}
            strokeWidth={2}
          />
          {isFavorite ? "즐겨찾기 해제" : "즐겨찾기 추가"}
        </button>
      )}
      <button
        onClick={() => onPick("delete")}
        className="w-full h-12 rounded-xl text-left px-4 text-[15px] font-medium text-red-600 hover:bg-red-50 active:scale-[0.98] transition flex items-center gap-3"
      >
        <Trash2 className="h-[18px] w-[18px] text-red-600" strokeWidth={2} />
        삭제
      </button>
      <button
        onClick={onClose}
        className="mt-2 w-full h-12 rounded-xl text-[15px] font-semibold text-neutral-500 border border-neutral-200 active:scale-[0.98] transition"
      >
        취소
      </button>
    </SheetShell>
  );
}

function SlotSheet({
  item,
  onClose,
  onPick,
}: {
  item: LogItem;
  onClose: () => void;
  onPick: (slot: MealSlot) => void;
}) {
  return (
    <SheetShell onClose={onClose} title="슬롯 변경" subtitle={item.foodName}>
      {MEAL_SLOT_ORDER.map((slot) => {
        const meta = MEAL_SLOT_META[slot];
        const active = item.slot === slot;
        return (
          <button
            key={slot}
            onClick={() => onPick(slot)}
            className={`w-full h-12 rounded-xl text-left px-4 text-[15px] font-medium active:scale-[0.98] transition flex items-center gap-2.5 ${
              active ? "bg-neutral-100 text-neutral-900" : "text-neutral-800 hover:bg-neutral-50"
            }`}
          >
            <meta.Icon className="h-4 w-4 text-neutral-500" strokeWidth={2.2} />
            {meta.label}으로
          </button>
        );
      })}
      <button
        onClick={onClose}
        className="mt-2 w-full h-12 rounded-xl text-[15px] font-semibold text-neutral-500 border border-neutral-200 active:scale-[0.98] transition"
      >
        취소
      </button>
    </SheetShell>
  );
}

function SheetShell({
  onClose,
  title,
  subtitle,
  children,
}: {
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div role="dialog" aria-modal="true" data-state="open" className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-[375px] rounded-t-2xl bg-white p-4 pb-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-neutral-200" />
        <div className="px-2 pb-2 text-[13px] text-neutral-500">
          {title}
          {subtitle ? (
            <> · <span className="text-neutral-800 font-medium">{subtitle}</span></>
          ) : null}
        </div>
        <div className="space-y-1">{children}</div>
      </motion.div>
    </div>
  );
}

/* ----------------------------- Edit Quantity ----------------------------- */

function foodRowToBaseline(row: FoodRow): Baseline {
  return {
    name: row.name,
    kcal: row.kcal,
    carb: row.carb_g,
    protein: row.protein_g,
    fat: row.fat_g,
    serving_g: row.serving_g,
  };
}

function itemFallbackBaseline(item: LogItem): Baseline {
  const itemKcal = Math.round(item.carbs * 4 + item.protein * 4 + item.fat * 9);
  const approxG = Math.max(1, Math.round(item.carbs + item.protein + item.fat));
  return {
    name: item.foodName,
    kcal: itemKcal,
    carb: item.carbs,
    protein: item.protein,
    fat: item.fat,
    serving_g: approxG,
  };
}

function EditQuantitySheet({
  item,
  onClose,
  onConfirm,
}: {
  item: LogItem;
  onClose: () => void;
  onConfirm: (newEntries: BubbleEntry[]) => void;
}) {
  // Start with name-based lookup (presets + localStorage) as initial value
  const nameBaseline = useMemo(() => findBaselineByName(item.foodName), [item.foodName]);
  const [baseline, setBaseline] = useState<Baseline>(
    () => nameBaseline ?? itemFallbackBaseline(item),
  );

  // On mount: if food_id is available, fetch from cloud foods and use that row as baseline
  useEffect(() => {
    if (!item.food_id) return;
    let cancelled = false;
    cloudRepository.foods.list().then((rows) => {
      if (cancelled) return;
      const row = rows.find((r) => r.id === item.food_id);
      if (row) setBaseline(foodRowToBaseline(row));
    }).catch(() => {
      // Network/auth failure — keep current baseline (name-based or fallback)
    });
    return () => { cancelled = true; };
  }, [item.food_id]);

  // Compute current grams from any non-zero macro ratio.
  function computeGrams(b: Baseline): number {
    const candidates: [number, number][] = [
      [item.carbs, b.carb],
      [item.protein, b.protein],
      [item.fat, b.fat],
    ];
    for (const [cur, base] of candidates) {
      if (base > 0 && cur > 0) return Math.round(b.serving_g * (cur / base));
    }
    return b.serving_g;
  }

  // 편집 기준 g = 원본 로그의 저장 grams(있으면). 없으면 매크로 비율로 역산(구버전 로그).
  // 이 값에서 선형 스케일하므로 음식 1인분 기준이 바뀌어도 영향 없음.
  const refGrams = item.logGrams && item.logGrams > 0 ? item.logGrams : computeGrams(baseline) || 1;
  // 편집 기준 kcal = 원본 로그의 공식 kcal(있으면). 없으면 매크로 Atwater.
  const refKcal =
    item.logKcal != null && item.logKcal > 0
      ? item.logKcal
      : Math.round(item.carbs * 4 + item.protein * 4 + item.fat * 9);

  const [mode, setMode] = useState<"serving" | "gram">("gram");
  const [qtyStr, setQtyStr] = useState(() => String(Math.round(refGrams) || 100));

  // mode 토글 시 현재 값을 새 단위로 비례 환산 (인분↔g은 음식 1인분 baseline.serving_g 기준)
  function switchMode(next: "serving" | "gram") {
    if (next === mode) return;
    const cur = parseFloat(qtyStr) || 0;
    if (next === "serving") {
      const servings = baseline.serving_g > 0 ? cur / baseline.serving_g : 1;
      setQtyStr(String(Math.round(servings * 100) / 100));
    } else {
      setQtyStr(String(Math.round(cur * baseline.serving_g)));
    }
    setMode(next);
  }

  const qty = parseFloat(qtyStr) || 0;
  // 현재 g (인분 모드는 음식 1인분 기준으로 환산)
  const grams = mode === "gram" ? qty : qty * baseline.serving_g;
  // 로그 자체 값에서 g 비례로 스케일 — 매크로·kcal 모두 일관.
  const gmult = refGrams > 0 ? grams / refGrams : 0;
  const carb = Math.round(item.carbs * gmult * 10) / 10;
  const protein = Math.round(item.protein * gmult * 10) / 10;
  const fat = Math.round(item.fat * gmult * 10) / 10;
  const kcal = Math.round(refKcal * gmult);
  const disabled = qty <= 0;

  function handleConfirm() {
    const macros: Record<Macro, number> = { carbs: carb, protein: protein, fat: fat };
    const newEntries: BubbleEntry[] = [];
    (["carbs", "protein", "fat"] as Macro[]).forEach((m, i) => {
      const g = Math.round(macros[m] * 10) / 10;
      if (g > 0) {
        newEntries.push({
          id: `${item.foodLogId}-${i}`,
          foodLogId: item.foodLogId,
          macro: m,
          grams: g,
          foodName: item.foodName,
          addedAt: item.addedAt,
          meal_slot: item.slot,
          // 공식 kcal·실제 grams를 함께 전달 — 저장 시 매크로 재계산 대신 이 값을 사용.
          logKcal: kcal,
          logGrams: Math.round(grams),
        });
      }
    });
    onConfirm(newEntries);
  }

  return (
    <div role="dialog" aria-modal="true" data-state="open" className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-[375px] rounded-t-2xl bg-white p-5 pb-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-neutral-200" />
        <h3 className="text-base font-bold text-neutral-900">{baseline.name}</h3>
        <p className="text-xs text-neutral-400 mt-0.5">
          {baseline.kcal} kcal / {baseline.serving_g}g 기준
        </p>

        <div className="mt-4 grid grid-cols-2 gap-1 rounded-lg bg-neutral-100 p-1">
          {(["serving", "gram"] as const).map((m) => (
            <button
              key={m}
              onClick={() => switchMode(m)}
              className={`h-8 rounded-md text-sm font-medium transition ${
                mode === m ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500"
              }`}
            >
              {m === "serving" ? "인분" : "그램(g)"}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            value={qtyStr}
            onChange={(e) => setQtyStr(e.target.value)}
            min={0}
            step={mode === "serving" ? 0.5 : 10}
            className="flex-1 h-12 px-3 rounded-xl border border-neutral-200 text-base font-semibold text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
          <span className="text-sm text-neutral-500 w-10 text-center">
            {mode === "serving" ? "인분" : "g"}
          </span>
        </div>

        <div className="mt-4 rounded-xl bg-neutral-50 px-4 py-3">
          <div className="text-xs text-neutral-500">예상 영양</div>
          <div className="mt-1 text-sm font-medium text-neutral-800">
            <span style={{ color: "#b58a00" }}>탄 {carb}g</span>
            <span className="mx-2 text-neutral-300">·</span>
            <span style={{ color: "#d63838" }}>단 {protein}g</span>
            <span className="mx-2 text-neutral-300">·</span>
            <span style={{ color: "#3478d6" }}>지 {fat}g</span>
          </div>
          <div className="mt-1 text-xs text-neutral-500">{kcal} kcal</div>
        </div>

        <button
          disabled={disabled}
          onClick={handleConfirm}
          className="mt-5 w-full h-12 rounded-xl bg-neutral-900 text-white text-sm font-semibold disabled:opacity-40 active:scale-95 transition"
        >
          수정하기
        </button>
      </motion.div>
    </div>
  );
}
